import { createMutation, createTriggerResource } from '@magicdoorinc/ui';
import { createMagicDoorContext } from '~/contexts/utils';
import { ManagementFeesRepository } from '~/repositories/managementFeesRepository';
import type {
  AddManagementFeeCreditDto,
  AddManagementFeesDto,
  AdjustManagementFeeCreditDto,
  CalculateManagementFeesDto,
  GetManagementFeeCreditsParams,
  GetPaginatedManagementFeesParams,
} from '~/swagger/portal/Api';

const managementFeesRepo = new ManagementFeesRepository();

export const [ManagementFeeProvider, useManagementFee] = createMagicDoorContext('ManagementFee', () => {
  const [filtered, setFilter, actions] = createTriggerResource((filter: GetPaginatedManagementFeesParams) =>
    managementFeesRepo.getManagementFees(filter)
  );

  const addManagementFee = createMutation(async (payload: AddManagementFeesDto) => {
    const response = await managementFeesRepo.addManagementFee(payload);
    actions.refetch();
    return response;
  });

  const getManagementFee = createMutation(async (managementFeeId: string) => {
    const response = await managementFeesRepo.getManagementFee(managementFeeId);
    return response;
  });

  const deleteManagementFee = createMutation(async (managementFeeId: string) => {
    await managementFeesRepo.deleteManagementFee(managementFeeId);
    actions.refetch();
  });

  const getManagementFeeCredits = createMutation(async (filter?: GetManagementFeeCreditsParams) => {
    const response = await managementFeesRepo.getManagementFeeCredits(filter);
    return response;
  });

  const addManagementFeeCredit = createMutation(async (payload: AddManagementFeeCreditDto) => {
    const response = await managementFeesRepo.addManagementFeeCredit(payload);
    actions.refetch();
    return response;
  });

  const getManagementFeeCredit = createMutation(async (managementFeeCreditId: string) => {
    const response = await managementFeesRepo.getManagementFeeCredit(managementFeeCreditId);
    return response;
  });

  const updateManagementFeeCredit = createMutation(
    async (params: { managementFeeCreditId: string; payload: AdjustManagementFeeCreditDto }) => {
      const response = await managementFeesRepo.updateManagementFeeCredit(params.managementFeeCreditId, params.payload);
      actions.refetch();
      return response;
    }
  );

  const deleteManagementFeeCredit = createMutation(async (managementFeeCreditId: string) => {
    await managementFeesRepo.deleteManagementFeeCredit(managementFeeCreditId);
    actions.refetch();
  });

  const [calculatedManagementFees, calculateManagementFees, calculatedManagementFeesActions] = createTriggerResource(
    async (payload: CalculateManagementFeesDto) => {
      const response = await managementFeesRepo.calculateManagementFees(payload);
      return {
        ...response,
        periods: (response.periods ?? []).filter((p) => (p.managementFee ?? 0) !== 0),
      };
    }
  );

  return {
    filtered: filtered,
    setFilter: (filter: GetPaginatedManagementFeesParams) => setFilter(filter),
    addManagementFee,
    deleteManagementFee,
    getManagementFee,
    getManagementFeeCredits,
    addManagementFeeCredit,
    getManagementFeeCredit,
    updateManagementFeeCredit,
    deleteManagementFeeCredit,
    calculatedManagementFees,
    calculateManagementFees,
    calculatedManagementFeesActions,
  };
});
